import { useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { isAuthenticated } from '../lib/auth.js'
import { confirmDeviceCode } from '../lib/api.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import RecoveryBlock from '../components/help/RecoveryBlock.jsx'
import useMe from '../hooks/useMe.js'
import { PAGE_FORM } from '../lib/layout.js'
import { BTN_PRIMARY_BLOCK } from '../lib/ui.js'

// Device-code confirmation page for the MCP `sign_in` flow (#82, repathed
// in #152). The MCP client opens https://next.actianlabs.com/link?code=XXXX-YYYY
// in the user's browser; the user signs in if needed (?return= bounces back
// here), then confirms — which POSTs to /api/v1/auth/device-code/confirm and
// flips the pending login to confirmed in next-services.
//
// On error, ErrorAlert renders the server message and a RecoveryBlock below
// it offers per-kind recovery copy + a link back to the help page (#152).
//
// Visual language mirrors SignInPage / RegisterPage (form rail, centered
// card, single primary action, ErrorAlert on failure).
export default function LinkDevicePage() {
  const [params] = useSearchParams()
  const codeParam = params.get('code') || ''
  const { me } = useMe()
  const [userCode, setUserCode] = useState(codeParam)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [errorKind, setErrorKind] = useState(null)
  const [confirmed, setConfirmed] = useState(false)

  // Synchronous auth gate — if no bearer, bounce to /signin?return=...
  // useMe is async; we don't wait on it for the redirect decision (no flash
  // of "signed-out then in" while /me loads). All hooks above must run on
  // every render to satisfy the rules-of-hooks lint.
  if (!isAuthenticated()) {
    const returnTo = `/link${codeParam ? `?code=${encodeURIComponent(codeParam)}` : ''}`
    return <Navigate to={`/signin?return=${encodeURIComponent(returnTo)}`} replace />
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!userCode.trim()) {
      setError('User code is required.')
      return
    }
    setSubmitting(true)
    setError(null)
    setErrorKind(null)
    try {
      await confirmDeviceCode(userCode.trim().toUpperCase())
      setConfirmed(true)
    } catch (err) {
      setError(err.message || 'Confirmation failed.')
      setErrorKind(err.kind || 'unknown')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className={`${PAGE_FORM} pb-12 pt-12`}>
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/60 sm:p-8">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Link a device</h1>
        <p className="mt-2 text-sm text-slate-600">
          Confirm the user code shown by <span className="font-medium">Claude Desktop</span>{' '}
          to finish signing in. After you confirm, return to Claude Desktop — the tool will
          continue automatically.
        </p>
        {me?.email ? (
          <p className="mt-2 text-xs text-slate-500">
            Signed in as <span className="font-medium text-slate-700">{me.email}</span>.
          </p>
        ) : null}

        {error ? (
          <div className="mt-4 space-y-3">
            <ErrorAlert title="Confirmation failed" message={error} source="LinkDevicePage" />
            {errorKind ? <RecoveryBlock kind={errorKind} userCode={userCode} /> : null}
          </div>
        ) : null}

        {confirmed ? (
          <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
            <p className="font-medium">Device confirmed.</p>
            <p className="mt-1">Return to Claude Desktop to continue.</p>
            <Link
              to="/admin"
              /* #354 exception: emerald, not brand. This is the confirmed-
                 success state of the device-linking flow, and colouring it like
                 every other primary action would lose exactly the signal it
                 exists to carry. */
              className="mt-3 inline-flex min-h-[36px] items-center rounded-full bg-emerald-700 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-800"
            >
              Go to dashboard
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3" noValidate>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">User code</span>
              <input
                type="text"
                required
                autoComplete="off"
                spellCheck={false}
                value={userCode}
                onChange={(e) => setUserCode(e.target.value)}
                readOnly={Boolean(codeParam)}
                placeholder="ABCD-1234"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-base tracking-widest placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
            </label>
            <button
              type="submit"
              disabled={submitting || !userCode.trim()}
              className={BTN_PRIMARY_BLOCK}
            >
              {submitting ? 'Confirming…' : 'Confirm device'}
            </button>
          </form>
        )}
      </div>
    </main>
  )
}
